import { IMessageCardProps, MessageCard } from "azure-devops-ui/MessageCard";
import { css } from "azure-devops-ui/Util";
import React from "react";
import { EventContext } from "../../../common/contexts/event";
import { FeatureContext } from "../../../common/contexts/feature";
import { SettingsContext } from "../../../common/contexts/settings";
import { executeOperation } from "../../../common/utilities/operation";
import { IEventDispatch } from "../../../common/utilities/platformdispatch";
import { getUserEmailPreferences } from "../../api/useremailpreferences";

import "./notificationgroup.css";

const { GoToNotificationButton, MOJNotificationBannerText } = window.Resources.AllPhotos;

interface INotificationBannerCardProps extends IMessageCardProps {
  content: React.ReactNode;
}

interface IBanner {
  /**
   * Optional function used to derive the active state of the banner from a
   * backend call or any custom logic.
   * If it is not provided, the banner is active by default.
   */
  active?: (eventDispatch: IEventDispatch) => Promise<boolean>;

  /**
   * Optional dependencies array indicating what features need to be on in order to display the banner
   * If not provided or empty, there is no dependencies.
   */
  featureDependencies?: string[];

  /**
   * Optional getCardProps can used to form a custom props of the banner.
   */
  getCardProps: (eventContext: IEventDispatch) => INotificationBannerCardProps;

  /**
   * Optional handler for any dismissal of the button
   */
  onDismiss?: () => void;

  /**
   * Optional array to hold all the page names this banner can be shown
   * If not provide (Undefined): this banner is available for all pages
   * If empty array is provided: this banner is not available for any page
   *
   */
  pagesToSupport?: string[];

  /**
   * Optional timeout determines how many days the banner will show up again after dimissing it.
   * 0 or undefined mean only show it once (no expiry).
   * Noted, due to our current limitation that we have no way to store this, the banner will
   * show up again when the cache is been cleared.
   */
  timeout?: number;
}
/**
 * Defines all the availble banners for photos app.
 */
export const allBanners: { [key: string]: IBanner } = {
  mojemail: {
    active: (eventDispatch: IEventDispatch) => {
      return executeOperation("getUserEmailPreferences", eventDispatch, (createRequest) => {
        const _getUserEmailPreferences = createRequest(getUserEmailPreferences, { name: "getUserEmailPreferences" });
        return _getUserEmailPreferences()
          .then((userEmailPreferences) => {
            // The response contains the on/off status for email notifications.
            // Porperty WeekendRecap(onedrive) or albumSuggestion(mpc-migrated) is to determine if onthisday email setting is on or not.
            // The Moj notification banner is to remind user to turn on the setting if it is off.
            // Hence, when the value is true (user has already enabled the notification), hide the moj banner.
            return !(userEmailPreferences?.WeekendRecap || userEmailPreferences?.albumSuggestion);
          })
          .catch(() => {
            // Set it inactive if error appears
            return false;
          });
      });
    },
    pagesToSupport: ["allphotosview"],
    timeout: 90,
    getCardProps: (eventContext) => {
      return {
        buttonProps: [
          {
            text: GoToNotificationButton,
            href: "https://onedrive.live.com/?v=NotificationOptions",
            onClick: () => {
              eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "goToNotificationSettingPage" });
            },
            primary: true
          }
        ],
        className: "moj-banner-no-shadow",
        content: MOJNotificationBannerText,
        iconProps: { iconName: "FavoriteStarFill" }
      };
    },
    featureDependencies: ["EnableMOJNotificationBanner"]
  }
};

export interface INotificationGroupProps {
  /**
   * Optional css className added to the root element of the notification group.
   */
  className?: string;
  /**
   * Maximum number of messages that are shown at a given time.
   *
   * @default 1
   */
  maxVisible?: number;

  /**
   * The name of the page that the banner group is going to show
   *
   */
  pageName: string;
}

export function NotificationGroup(props: INotificationGroupProps): React.ReactElement | null {
  const { className, maxVisible = 1, pageName } = props;

  const eventContext = React.useContext(EventContext);
  const featureContext = React.useContext(FeatureContext);
  const settingsContext = React.useContext(SettingsContext);

  const [activeBanners, setActiveBanners] = React.useState<string[]>([]);

  React.useState(() => {
    const notificationsExpiry = settingsContext.getSetting<{ [key: string]: number }>("notificationsExpiry") || {};

    let availableBannersForPage: string[] = [];
    let activePromises = [];

    let hasSettingExpired = false;

    for (let banner of Object.keys(allBanners)) {
      const curr = allBanners[banner];

      if (
        (curr.featureDependencies && curr.featureDependencies.some((feature: string) => featureContext.featureEnabled(feature).value === false)) ||
        (curr.pagesToSupport && !curr.pagesToSupport.includes(pageName))
      ) {
        continue;
      }

      // Check if the banner was dimissed by user and has an expiry time
      const expiration = notificationsExpiry[banner];
      if (expiration === 0 || Date.now() < expiration) {
        // When this banner only shows up once, and it is not expired yet, keep hiding this banner and continue on to the next banner
        continue;
      }

      // Expired, remove the config from the localstorage
      hasSettingExpired = true;
      delete notificationsExpiry[banner];

      // Add the banner to the available list
      availableBannersForPage.push(banner);

      // Prepare a promise array to resolve active status for all available banners
      // If active function is not specified, it is active by default, use a promise so we want to resolve all
      // at the same time later for one UI rendering
      activePromises.push(curr.active ? curr.active(eventContext) : Promise.resolve(true));

      // Have reached the max allowed notification, stop looping the rest of banners
      if (availableBannersForPage.length === maxVisible) {
        break;
      }
    }

    // Update localStorage if there are expired settings
    if (hasSettingExpired) {
      settingsContext.setSetting("notificationsExpiry", notificationsExpiry);
    }

    Promise.allSettled(activePromises)
      .then((results) => {
        setActiveBanners(
          availableBannersForPage.filter((_, index: number) => {
            const result = results[index];
            if (result.status === "fulfilled") {
              return result.value === true;
            }
            return false;
          })
        );
      })
      .catch(() => {
        // if something failed, do nothing (not showing any banners)
      });
  });

  return activeBanners.length > 0 ? (
    <div className={css("photo-notification-group flex-column", className)}>
      {activeBanners.map((banner: string) => {
        const bannerInfo = allBanners[banner];
        const customProps = bannerInfo.getCardProps?.(eventContext);
        return (
          <MessageCard
            buttonProps={customProps?.buttonProps}
            className={customProps?.className}
            iconProps={customProps?.iconProps}
            key={banner}
            messageBarClassName={css("notification-banner", customProps?.messageBarClassName)}
            onDismiss={() => {
              // Set the time to expire in the localStorage
              // if timeout is not provided, set the value to 0 indicating it only shows up once and never expires.
              settingsContext.mergeSetting("notificationsExpiry", {
                [banner]: bannerInfo.timeout ? Date.now() + bannerInfo.timeout * 1000 * 60 * 60 * 24 : 0
              });

              // Remove from the activeBanners to update the UI
              const messageIndex = activeBanners.indexOf(banner);
              if (messageIndex >= 0) {
                const newActiveBanners = activeBanners.filter((_, index) => index !== messageIndex);
                setActiveBanners(newActiveBanners);
              }

              eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "dismissNotificationBanner", value: banner });
              customProps?.onDismiss?.();
            }}
            severity={customProps?.severity}
          >
            {customProps?.content}
          </MessageCard>
        );
      })}
    </div>
  ) : null;
}
